import React from 'react';
import { Train, Plane, MapPinned, Lightbulb, Image as ImageIcon } from 'lucide-react';

function tagPillClass(tone) {
  switch (tone) {
    case 'secondary':
      return 'bg-secondary text-on-secondary';
    case 'tertiary':
      return 'bg-tertiary text-white';
    default:
      return 'bg-primary text-on-primary';
  }
}

function SectionIcon({ type }) {
  if (type === 'numbered_points') return <Train className="h-9 w-9 sm:h-10 sm:w-10 text-primary shrink-0" aria-hidden />;
  if (type === 'image_grid') return <MapPinned className="h-9 w-9 text-secondary shrink-0" aria-hidden />;
  if (type === 'callout') return <Lightbulb className="h-9 w-9 text-primary shrink-0" aria-hidden />;
  if (type === 'hero_image_section') return <ImageIcon className="h-9 w-9 text-primary shrink-0" aria-hidden />;
  return <Plane className="h-8 w-8 text-secondary shrink-0 opacity-80" aria-hidden />;
}

/**
 * @param {{ section: object, sectionIndex: number }} props
 */
export function GuideRichSection({ section, sectionIndex }) {
  if (!section) return null;

  if (section.type === 'feature_grid' && Array.isArray(section.cards)) {
    return (
      <section className="mb-14 md:mb-16" aria-labelledby={`feature-grid-${sectionIndex}`}>
        <h2 id={`feature-grid-${sectionIndex}`} className="sr-only">
          Highlights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {section.cards.map((c) => (
            <div
              key={c.title}
              className={`bg-surface-container-low dark:bg-slate-800/80 p-6 rounded-2xl border-l-4 ${
                c.accent === 'secondary' ? 'border-secondary' : 'border-primary'
              }`}
            >
              <h3 className={`font-bold mb-2 font-headline ${c.accent === 'secondary' ? 'text-secondary' : 'text-primary'}`}>{c.title}</h3>
              <p className="text-sm text-on-surface-variant dark:text-slate-300 leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (section.type === 'image_grid' && Array.isArray(section.items)) {
    return (
      <section className="mb-14 md:mb-16" aria-labelledby={`image-grid-${sectionIndex}`}>
        <div className="flex items-center gap-3 mb-8">
          <SectionIcon type="image_grid" />
          <h2 id={`image-grid-${sectionIndex}`} className="text-3xl sm:text-4xl font-black tracking-tight text-on-surface dark:text-white font-headline">
            {section.title || 'Neighborhood & venue context'}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {section.items.map((item) => (
            <div key={item.title} className="group">
              <div className="relative h-56 sm:h-64 rounded-3xl overflow-hidden mb-4 border border-outline-variant/20 dark:border-slate-700">
                <img
                  src={item.image}
                  alt={item.imageAlt || item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {item.tag ? (
                  <div
                    className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${tagPillClass(item.tagTone)}`}
                  >
                    {item.tag}
                  </div>
                ) : null}
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 text-on-surface dark:text-white font-headline">{item.title}</h3>
              <p className="text-on-surface-variant dark:text-slate-400 text-sm leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (section.type === 'callout') {
    const variant = section.variant === 'tertiary' ? 'tertiary' : 'primary';
    return (
      <section className="mb-14 md:mb-16" aria-label={section.title}>
        <div
          className={`rounded-3xl p-6 sm:p-8 relative overflow-hidden ${
            variant === 'tertiary'
              ? 'bg-tertiary/10 dark:bg-tertiary/15 border border-tertiary/20'
              : 'bg-primary-container/30 dark:bg-primary-dim/25 border border-primary/15'
          }`}
        >
          <div className="absolute -right-8 -top-8 opacity-10 pointer-events-none" aria-hidden>
            <Lightbulb className="h-32 w-32 text-primary" />
          </div>
          <div className="flex items-start gap-4 relative">
            <div className="bg-primary text-on-primary p-3 rounded-xl shrink-0">
              <Lightbulb className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-on-primary-container dark:text-primary-container mb-2 uppercase tracking-tight font-headline">
                {section.title}
              </h3>
              <p className="text-on-surface dark:text-slate-200 font-medium leading-relaxed">{section.text}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (section.type === 'numbered_points' && Array.isArray(section.items)) {
    return (
      <section className="mb-14 md:mb-16 bg-surface-container-low dark:bg-slate-800/60 rounded-3xl p-6 sm:p-8 border border-outline-variant/15 dark:border-slate-700">
        <div className="flex items-center gap-4 mb-6">
          <SectionIcon type="numbered_points" />
          <h2 className="text-2xl sm:text-3xl font-bold text-on-surface dark:text-white font-headline">
            {section.title || 'Tournament-week rhythm'}
          </h2>
        </div>
        {section.intro ? <p className="text-on-surface-variant dark:text-slate-300 mb-6 leading-relaxed">{section.intro}</p> : null}
        <div className="space-y-6">
          {section.items.map((item, i) => (
            <div key={item.title} className="flex gap-4 sm:gap-6">
              <div className="text-2xl sm:text-3xl font-black text-outline-variant/40 dark:text-slate-600 font-headline w-10 shrink-0 text-right">
                {String(i + 1).padStart(2, '0')}
              </div>
              <div>
                <h4 className="font-bold text-lg text-on-surface dark:text-white font-headline">{item.title}</h4>
                <p className="text-on-surface-variant dark:text-slate-300 mt-1 leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (section.type === 'hero_image_section' && section.image) {
    return (
      <section className="mb-14 md:mb-16" aria-label={section.title || 'Venue spotlight'}>
        {section.title ? (
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-6 text-on-surface dark:text-white font-headline">{section.title}</h2>
        ) : null}
        <div className="relative h-[280px] sm:h-[360px] md:h-[400px] rounded-3xl overflow-hidden border border-outline-variant/20 dark:border-slate-700">
          <img src={section.image} alt={section.alt || ''} className="w-full h-full object-cover" />
          {(section.overlayTitle || section.overlayText) && (
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent flex items-end p-6 sm:p-8">
              <div className="text-white max-w-xl">
                {section.overlayTitle ? <h3 className="text-xl sm:text-2xl font-bold font-headline mb-2">{section.overlayTitle}</h3> : null}
                {section.overlayText ? <p className="text-sm sm:text-base opacity-95 leading-relaxed">{section.overlayText}</p> : null}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  if (!section.title) return null;

  return (
    <section className="mb-12 md:mb-14" aria-labelledby={`sec-${sectionIndex}`}>
      <h2 id={`sec-${sectionIndex}`} className="text-2xl sm:text-3xl font-bold text-primary dark:text-primary-container mb-5 font-headline tracking-tight">
        {section.title}
      </h2>
      <div className="prose prose-lg max-w-none text-on-surface-variant dark:text-slate-300 leading-relaxed font-body">
        {(section.paragraphs || []).map((p, i) => (
          <p key={i} className="mb-5 last:mb-0">
            {p}
          </p>
        ))}
      </div>
    </section>
  );
}

export default GuideRichSection;
